/* LSA Product Hotspots · pins, notes, legend and counter.
   Behaviour follows the Distil Union image-hotspots section; see the comment at
   the top of sections/lsa-product-hotspots.liquid. */
if (!customElements.get('lsa-product-hotspots')) {
  customElements.define(
    'lsa-product-hotspots',
    class LsaProductHotspots extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('[data-lsa-phs-stage]');
        if (!this.stage) return;
        this.pins = Array.from(this.querySelectorAll('[data-lsa-phs-pin]'));
        this.notes = Array.from(this.querySelectorAll('[data-lsa-phs-note]'));
        this.rows = Array.from(this.querySelectorAll('[data-lsa-phs-row]'));
        this.current = this.querySelector('[data-lsa-phs-current]');
        this.segment = this.querySelector('[data-lsa-phs-segment]');
        this.openIndex = 0;
        this.locked = false;
        this.hoverMQ = window.matchMedia('(hover: hover) and (min-width: 750px)');
        this.gap = 16;
        this.edge = 12;

        this.onDocClick = this.onDocClick.bind(this);
        this.onKey = this.onKey.bind(this);
        this.onResize = this.onResize.bind(this);

        this.pins.forEach((pin) => {
          const i = Number(pin.dataset.index);
          pin.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleLock(i);
          });
          pin.addEventListener('mouseenter', () => this.hoverOpen(i));
          pin.addEventListener('mouseleave', () => this.hoverClose());
        });

        this.notes.forEach((note) => {
          note.addEventListener('click', (e) => e.stopPropagation());
          note.addEventListener('mouseenter', () => clearTimeout(this.hideTimer));
          note.addEventListener('mouseleave', () => this.hoverClose());
          const close = note.querySelector('[data-lsa-phs-close]');
          if (close) close.addEventListener('click', () => this.close(true));
        });

        this.rows.forEach((row) => {
          const i = Number(row.dataset.index);
          row.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleLock(i);
          });
          row.addEventListener('mouseenter', () => this.hoverOpen(i));
          row.addEventListener('mouseleave', () => this.hoverClose());
        });

        // Theme editor: selecting a Hotspot block opens its note, so the
        // merchant sees what they are editing.
        this.onBlockSelect = (e) => {
          const note = this.notes.find((n) => n.id === `LsaPhsNote-${e.detail.blockId}`);
          if (!note) return;
          this.locked = true;
          this.open(Number(note.dataset.index));
        };

        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.removeEventListener('resize', this.onResize);
      }

      hoverOpen(i) {
        if (!this.hoverMQ.matches || this.locked) return;
        clearTimeout(this.hideTimer);
        this.open(i);
      }

      hoverClose() {
        if (!this.hoverMQ.matches || this.locked) return;
        // A short grace period lets the pointer travel from pin to note.
        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => this.close(false), 140);
      }

      toggleLock(i) {
        if (this.openIndex === i && this.locked) {
          this.close(true);
          return;
        }
        this.locked = true;
        this.open(i);
      }

      open(i) {
        if (this.openIndex === i) {
          this.place(i);
          return;
        }
        this.close(false, true);
        const pin = this.pins.find((p) => Number(p.dataset.index) === i);
        const note = this.notes.find((n) => Number(n.dataset.index) === i);
        if (!pin || !note) return;

        this.openIndex = i;
        note.hidden = false;
        this.place(i);
        requestAnimationFrame(() => note.classList.add('is-open'));
        pin.setAttribute('aria-expanded', 'true');
        this.rows.forEach((r) => r.setAttribute('aria-expanded', Number(r.dataset.index) === i ? 'true' : 'false'));

        if (this.current) this.current.textContent = String(i).padStart(2, '0');
        if (this.segment) {
          this.segment.style.setProperty('--current', i);
          this.segment.classList.add('is-active');
        }
      }

      close(unlock, keepFooter) {
        if (unlock) this.locked = false;
        if (!this.openIndex) return;
        const i = this.openIndex;
        this.openIndex = 0;
        const pin = this.pins.find((p) => Number(p.dataset.index) === i);
        const note = this.notes.find((n) => Number(n.dataset.index) === i);
        if (pin) pin.setAttribute('aria-expanded', 'false');
        if (note) {
          note.classList.remove('is-open');
          note.hidden = true;
        }
        this.rows.forEach((r) => r.setAttribute('aria-expanded', 'false'));
        if (!keepFooter) {
          if (this.current) this.current.textContent = '00';
          if (this.segment) this.segment.classList.remove('is-active');
        }
      }

      // Position the note beside its pin, on the side with room, then clamp it
      // inside the plate. Under 750px the stylesheet docks it instead.
      place(i) {
        const note = this.notes.find((n) => Number(n.dataset.index) === i);
        const pin = this.pins.find((p) => Number(p.dataset.index) === i);
        if (!note || !pin) return;
        if (window.matchMedia('(max-width: 749px)').matches) {
          note.style.left = '';
          note.style.top = '';
          // Docked to the foot of the image, unless the image is too short to
          // hold it (a landscape image at phone width): then it opens just
          // below the image instead of covering the whole photograph.
          const plate = note.closest('[data-lsa-phs-stage]');
          const short = plate && plate.clientHeight < note.offsetHeight + 160;
          note.classList.toggle('lsa-phs__note--below', !!short);
          return;
        }
        note.classList.remove('lsa-phs__note--below');

        // With two plates, each note is placed within its own plate.
        const stage = note.closest('[data-lsa-phs-stage]') || this.stage;
        const W = stage.clientWidth;
        const H = stage.clientHeight;
        const px = pin.offsetLeft;
        const py = pin.offsetTop;
        const w = note.offsetWidth;
        const h = note.offsetHeight;
        const r = 22; // half the pin's tap target
        const g = this.gap;

        let anchor = note.dataset.anchor || 'auto';
        if (anchor === 'auto') {
          if (px + r + g + w <= W - this.edge) anchor = 'right';
          else if (px - r - g - w >= this.edge) anchor = 'left';
          else if (py + r + g + h <= H - this.edge) anchor = 'bottom';
          else anchor = 'top';
        }

        let left;
        let top;
        switch (anchor) {
          case 'left':
            left = px - r - g - w;
            top = py - h / 2;
            break;
          case 'top':
            left = px - w / 2;
            top = py - r - g - h;
            break;
          case 'bottom':
            left = px - w / 2;
            top = py + r + g;
            break;
          default:
            left = px + r + g;
            top = py - h / 2;
        }

        left = Math.max(this.edge, Math.min(left, W - w - this.edge));
        top = Math.max(this.edge, Math.min(top, H - h - this.edge));
        note.style.left = `${Math.round(left)}px`;
        note.style.top = `${Math.round(top)}px`;
      }

      // Any click that is not on a pin, a note or a legend row closes the note,
      // whether it lands elsewhere in the section or elsewhere on the page.
      onDocClick(e) {
        if (!this.openIndex) return;
        if (e.target.closest && e.target.closest('[data-lsa-phs-pin], [data-lsa-phs-note], [data-lsa-phs-row]')) return;
        this.close(true);
      }

      onKey(e) {
        if (e.key !== 'Escape' || !this.openIndex) return;
        const pin = this.pins.find((p) => Number(p.dataset.index) === this.openIndex);
        this.close(true);
        if (pin) pin.focus();
      }

      onResize() {
        if (this.openIndex) this.place(this.openIndex);
      }
    }
  );
}
